(() => {
  'use strict';

  const STEP_META = {
    1: { progress: '01 / 05 — YOUR STYLE', action: 'CONTINUE' },
    2: { progress: '02 / 05 — YOUR FIT', action: 'CONTINUE' },
    3: { progress: '03 / 05 — YOUR BRANDS', action: 'CONTINUE' },
    4: { progress: '04 / 05 — YOUR BUILD', action: 'REVIEW MY BUILD' },
    5: { progress: '05 / 05 — FINAL EDIT', action: 'COMPLETE MY BUILD' }
  };

  const CATEGORY_PATTERNS = {
    final: /note|social|instagram|tiktok|handle|anything else|tell us|stylist|brief|comment/i,
    brands: /brand|label|designer|which names/i,
    fit: /size|fit|waist|inseam|height|silhouette|oversized|regular|fitted|baggy|relaxed|measurement/i,
    build: /bottom|trouser|pant|jean|short|top|tee|t-shirt|shirt|sweat|hood|layer|jacket|coat|cap|hat|chain|necklace|ring|bag|tote|bracelet|accessor|piece|add[ -]?on|upgrade/i,
    style: /style|aesthetic|vibe|look|streetwear|skater|y2k|minimal|workwear|preppy|softboy/i
  };

  const GROUP_IGNORE_PATTERN = /step(s)?[-_ ]toggle|gpo-total|gpo-atc|quantity/i;

  function whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function unique(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function getGroupDescriptor(group) {
    const controls = Array.from(group.querySelectorAll('input, select, textarea'));
    return cleanText([
      group.getAttribute('class'),
      group.getAttribute('data-label'),
      group.querySelector('.label-content, .gpo-label, h2, h3, legend')?.textContent,
      ...controls.map((control) => `${control.name || ''} ${control.id || ''} ${control.value || ''}`)
    ].join(' '));
  }

  function groupStage(group) {
    const descriptor = getGroupDescriptor(group);
    if (CATEGORY_PATTERNS.final.test(descriptor)) return 5;
    if (CATEGORY_PATTERNS.brands.test(descriptor)) return 3;
    if (CATEGORY_PATTERNS.fit.test(descriptor)) return 2;
    if (CATEGORY_PATTERNS.build.test(descriptor)) return 4;
    if (CATEGORY_PATTERNS.style.test(descriptor)) return 1;

    const legacyClass = Array.from(group.classList).find((name) => name.startsWith('kg-mystery-form-group-'));
    const legacyStep = legacyClass ? Number(legacyClass.replace('kg-mystery-form-group-', '')) : 0;
    if (legacyStep >= 1 && legacyStep <= 5) return legacyStep;
    return 5;
  }

  function initStudio(root) {
    if (root.dataset.grStudioInitialised === 'true') return;
    root.dataset.grStudioInitialised = 'true';

    const productFormElement = root.closest('product-form');
    const form = root.closest('form');
    const launchButton = productFormElement?.querySelector('[data-gr-studio-launch]');
    const submitProxy = productFormElement?.querySelector('[data-gr-studio-submit]');
    const loading = root.querySelector('[data-gr-studio-loading]');
    const error = root.querySelector('[data-gr-studio-error]');
    const stages = root.querySelector('[data-gr-studio-stages]');
    const footer = root.querySelector('[data-gr-studio-footer]');
    const backButton = root.querySelector('[data-gr-studio-back]');
    const nextButton = root.querySelector('[data-gr-studio-next]');
    const progressCopy = root.querySelector('[data-gr-studio-progress-copy]');
    const progressBar = root.querySelector('[data-gr-studio-progress]');
    const announcer = root.querySelector('[data-gr-studio-announcer]');
    const retryButton = root.querySelector('[data-gr-studio-retry]');

    if (!productFormElement || !form || !launchButton || !submitProxy) return;

    document.documentElement.classList.add('gr-studio-product');

    let currentStep = 1;
    let ready = false;
    let opened = false;
    let lastFocusedElement = null;
    let waitTimer = null;
    let waitStartedAt = 0;
    let sourceContainer = null;
    let sourceGroups = [];

    const galleryImages = unique(
      Array.from(document.querySelectorAll('[id^="MediaGallery-"] img, [id^="GalleryThumbnails-"] img'))
        .map((image) => image.currentSrc || image.src)
    );

    function announce(message) {
      if (!announcer) return;
      announcer.textContent = '';
      window.requestAnimationFrame(() => { announcer.textContent = message; });
    }

    function openStudio() {
      lastFocusedElement = document.activeElement;
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('gr-studio-open');
      opened = true;
      updateStep(currentStep, false);
      window.requestAnimationFrame(() => root.classList.add('is-open'));

      if (!ready) waitForProductOptions(true);
      window.setTimeout(() => root.querySelector('.gr-studio__close')?.focus(), 80);
    }

    function closeStudio() {
      root.classList.remove('is-open');
      document.documentElement.classList.remove('gr-studio-open');
      opened = false;
      window.setTimeout(() => {
        root.hidden = true;
        root.setAttribute('aria-hidden', 'true');
      }, 220);
      if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
    }

    function candidateGroups(container) {
      return Array.from(container.querySelectorAll('.gpo-form__group')).filter((group) => {
        if (group.closest('[data-gr-studio]')) return false;
        if (group.parentElement?.closest('.gpo-form__group')) return false;
        if (GROUP_IGNORE_PATTERN.test(group.className)) return false;
        return Boolean(group.querySelector('input, select, textarea'));
      });
    }

    function showError() {
      if (ready) return;
      loading.hidden = true;
      stages.hidden = true;
      footer.hidden = true;
      error.hidden = false;
      root.classList.add('has-error');
      announce('Styling options could not be loaded.');
    }

    function waitForProductOptions(resetTimer = false) {
      window.clearTimeout(waitTimer);
      if (resetTimer || !waitStartedAt) waitStartedAt = Date.now();

      sourceContainer = form.querySelector('.gpo-container');
      sourceGroups = sourceContainer ? candidateGroups(sourceContainer) : [];

      if (sourceContainer && sourceGroups.length) {
        prepareStudio();
        return;
      }

      if (Date.now() - waitStartedAt >= 12000) {
        showError();
        return;
      }

      loading.hidden = false;
      error.hidden = true;
      waitTimer = window.setTimeout(() => waitForProductOptions(false), 250);
    }

    function prepareStudio() {
      sourceGroups.forEach((group, groupIndex) => {
        const stageNumber = groupStage(group);
        const slot = root.querySelector(`[data-gr-studio-slot="${stageNumber}"]`);
        if (!slot) return;

        group.dataset.grStudioGroup = String(stageNumber);
        group.classList.add('gr-studio__group');
        group.style.removeProperty('display');
        decorateGroup(group, stageNumber, groupIndex);
        slot.appendChild(group);
      });

      addExpansionControl(root.querySelector('[data-gr-studio-slot="1"]'), 8, 'More styles');
      addExpansionControl(root.querySelector('[data-gr-studio-slot="3"]'), 12, 'View all brands');

      sourceContainer.classList.add('gr-studio__source-is-ready');
      loading.hidden = true;
      error.hidden = true;
      stages.hidden = false;
      footer.hidden = false;
      root.classList.remove('has-error');
      root.classList.add('is-ready');
      ready = true;

      form.addEventListener('change', handleSelectionChange);
      form.addEventListener('input', handleSelectionChange);
      syncSelectionClasses();
      updateBuildTray();
      updateSummary();
      updateStep(currentStep, false);
      announce('Your styling options are ready.');
    }

    function decorateGroup(group, stageNumber, groupIndex) {
      const heading = group.querySelector('.label-content, .gpo-label, h2, h3, legend');
      if (heading) heading.classList.add('gr-studio__group-title');

      const choices = Array.from(group.querySelectorAll('.gpo-choicelist, .gpo-swatches'));
      choices.forEach((choice, choiceIndex) => {
        const control = choice.querySelector('input[type="radio"], input[type="checkbox"]');
        if (!control) return;

        choice.classList.add('gr-studio__choice');
        if (stageNumber === 1 || stageNumber === 4) choice.classList.add('gr-studio__choice--visual');
        if (stageNumber === 2) choice.classList.add('gr-studio__choice--fit');
        if (stageNumber === 3) choice.classList.add('gr-studio__choice--brand');

        const label = choice.querySelector('label');
        if (label) label.classList.add('gr-studio__choice-label');

        const image = choice.querySelector('img');
        if (image) {
          image.loading = stageNumber === 1 && groupIndex === 0 && choiceIndex < 4 ? 'eager' : 'lazy';
          image.decoding = 'async';
        } else if ((stageNumber === 1 || stageNumber === 4) && galleryImages.length) {
          const imageUrl = galleryImages[(groupIndex * 3 + choiceIndex) % galleryImages.length];
          choice.style.setProperty('--gr-choice-image', `url("${imageUrl.replace(/"/g, '%22')}")`);
          choice.classList.add('gr-studio__choice--has-fallback-image');
        }
      });

      group.querySelectorAll('select').forEach((select) => select.classList.add('gr-studio__select'));
      group.querySelectorAll('textarea, input[type="text"], input[type="email"]').forEach((input) => {
        input.classList.add('gr-studio__field');
      });
    }

    function addExpansionControl(slot, limit, label) {
      if (!slot) return;
      const choices = Array.from(slot.querySelectorAll('.gr-studio__choice'));
      if (choices.length <= limit) return;

      choices.slice(limit).forEach((choice) => choice.classList.add('gr-studio__choice--collapsed'));
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gr-studio__expand';
      button.textContent = `${label} +`;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        choices.slice(limit).forEach((choice) => choice.classList.toggle('gr-studio__choice--collapsed', expanded));
        button.textContent = expanded ? `${label} +` : 'Show fewer −';
      });
      slot.appendChild(button);
    }

    function handleSelectionChange() {
      syncSelectionClasses();
      updateBuildTray();
      updateSummary();
    }

    function syncSelectionClasses() {
      root.querySelectorAll('.gr-studio__choice').forEach((choice) => {
        const control = choice.querySelector('input[type="radio"], input[type="checkbox"]');
        const selected = Boolean(control?.checked);
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });
    }

    function groupTitle(group) {
      return cleanText(group.querySelector('.label-content, .gpo-label, h2, h3, legend')?.textContent)
        .replace(/\*$/, '') || 'Selection';
    }

    function optionLabel(control) {
      if (control instanceof HTMLSelectElement) {
        return cleanText(control.selectedOptions[0]?.textContent);
      }
      const label = control.id ? form.querySelector(`label[for="${CSS.escape(control.id)}"]`) : control.closest('label');
      return cleanText(label?.textContent || control.value).replace(/\s*[+−]?\s*[£$€]\s*\d[\d.,]*/g, '').trim();
    }

    function selectedValues(group) {
      const values = [];
      group.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.disabled) return;
        if (control instanceof HTMLInputElement && ['radio', 'checkbox'].includes(control.type)) {
          if (control.checked) values.push(optionLabel(control));
          return;
        }
        if (control instanceof HTMLSelectElement) {
          if (control.value) values.push(optionLabel(control));
          return;
        }
        if (control instanceof HTMLTextAreaElement || (control instanceof HTMLInputElement && ['text', 'email'].includes(control.type))) {
          if (cleanText(control.value)) values.push(cleanText(control.value));
        }
      });
      return unique(values);
    }

    function stageSelections(step) {
      return Array.from(root.querySelectorAll(`[data-gr-studio-group="${step}"]`)).flatMap((group) => selectedValues(group));
    }

    function updateBuildTray() {
      const tray = root.querySelector('[data-gr-studio-build-tray]');
      const items = root.querySelector('[data-gr-studio-build-items]');
      if (!tray || !items) return;

      const selected = stageSelections(4);
      items.innerHTML = '';
      selected.forEach((value) => {
        const chip = document.createElement('span');
        chip.textContent = value;
        items.appendChild(chip);
      });
      tray.hidden = selected.length === 0;
    }

    function updateSummary() {
      const summaryList = root.querySelector('[data-gr-studio-summary-list]');
      if (!summaryList) return;
      summaryList.innerHTML = '';

      const summaries = [
        { step: 1, label: 'Your Style' },
        { step: 2, label: 'Your Fit & Sizes' },
        { step: 3, label: 'Brands You Rate' },
        { step: 4, label: 'Your Selected Pieces' }
      ];

      summaries.forEach(({ step, label }) => {
        const item = document.createElement('div');
        item.className = 'gr-studio__summary-item';
        const copy = document.createElement('div');
        const name = document.createElement('span');
        const value = document.createElement('p');
        const edit = document.createElement('button');
        const selections = stageSelections(step);

        name.textContent = label;
        value.textContent = selections.length ? selections.join(', ') : 'Not selected';
        edit.type = 'button';
        edit.textContent = 'Edit';
        edit.addEventListener('click', () => updateStep(step));

        copy.append(name, value);
        item.append(copy, edit);
        summaryList.appendChild(item);
      });

      const totalContainer = root.querySelector('[data-gr-studio-summary-total]');
      const totalValue = root.querySelector('[data-gr-studio-total]');
      const totalSource = form.querySelector('.gpo-total');
      const totalText = cleanText(totalSource?.textContent);
      if (totalContainer && totalValue) {
        totalContainer.hidden = !totalText;
        totalValue.textContent = totalText;
      }
    }

    function validateStep(step) {
      const groups = Array.from(root.querySelectorAll(`[data-gr-studio-group="${step}"]`));
      let firstInvalid = null;

      groups.forEach((group) => {
        const required = group.classList.contains('required') || Boolean(group.querySelector('[required]'));
        if (!required) {
          group.classList.remove('has-gr-studio-error');
          return;
        }

        const controls = Array.from(group.querySelectorAll('input, select, textarea')).filter((control) => !control.disabled);
        const choiceControls = controls.filter((control) => control instanceof HTMLInputElement && ['radio', 'checkbox'].includes(control.type));
        let valid = true;

        if (choiceControls.length) {
          valid = choiceControls.some((control) => control.checked);
        } else {
          valid = controls.every((control) => !control.required || cleanText(control.value));
        }

        group.classList.toggle('has-gr-studio-error', !valid);
        if (!valid && !firstInvalid) firstInvalid = group;
      });

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.querySelector('input, select, textarea')?.focus({ preventScroll: true });
        announce(`Please complete ${groupTitle(firstInvalid)} before continuing.`);
        return false;
      }
      return true;
    }

    function updateStep(step, moveFocus = true) {
      currentStep = Math.min(5, Math.max(1, Number(step) || 1));
      const meta = STEP_META[currentStep];

      root.querySelectorAll('[data-gr-studio-stage]').forEach((panel) => {
        const active = Number(panel.dataset.grStudioStage) === currentStep;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      root.querySelectorAll('[data-gr-studio-visual]').forEach((panel) => {
        panel.classList.toggle('is-active', Number(panel.dataset.grStudioVisual) === currentStep);
      });

      progressCopy.textContent = meta.progress;
      progressBar.style.setProperty('--gr-progress', `${currentStep * 20}%`);
      backButton.hidden = currentStep === 1;
      nextButton.textContent = meta.action;
      nextButton.classList.toggle('is-final', currentStep === 5);

      if (currentStep === 5) updateSummary();
      if (moveFocus && opened) {
        root.querySelector(`[data-gr-studio-stage="${currentStep}"] h2`)?.focus?.({ preventScroll: true });
        root.querySelector('.gr-studio__experience')?.scrollTo({ top: 0, behavior: 'smooth' });
      }
      announce(meta.progress);
    }

    function completeBuild() {
      if (!ready || !validateStep(5)) return;
      form.dataset.grStudioCommit = 'true';
      nextButton.disabled = true;
      nextButton.classList.add('is-loading');
      announce('Adding your completed build to the cart.');
      submitProxy.click();
      window.setTimeout(() => {
        delete form.dataset.grStudioCommit;
        nextButton.disabled = false;
        nextButton.classList.remove('is-loading');
      }, 5000);
    }

    launchButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      openStudio();
    }, true);

    form.addEventListener('submit', (event) => {
      if (form.dataset.grStudioCommit === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openStudio();
    }, true);

    root.querySelectorAll('[data-gr-studio-close]').forEach((button) => button.addEventListener('click', closeStudio));
    retryButton?.addEventListener('click', () => {
      root.classList.remove('has-error');
      error.hidden = true;
      loading.hidden = false;
      waitForProductOptions(true);
    });
    backButton.addEventListener('click', () => updateStep(currentStep - 1));
    nextButton.addEventListener('click', () => {
      if (!ready) return;
      if (currentStep < 5) {
        if (validateStep(currentStep)) updateStep(currentStep + 1);
      } else {
        completeBuild();
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeStudio();
      if (event.key !== 'Tab') return;
      const focusable = Array.from(root.querySelectorAll('button:not([hidden]):not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href$="#build-my-outfit"]');
      if (!anchor) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.pathname !== window.location.pathname) return;
      event.preventDefault();
      openStudio();
    });

    const observer = new MutationObserver(() => {
      if (!ready) waitForProductOptions(false);
    });
    observer.observe(form, { childList: true, subtree: true });

    waitForProductOptions(true);
    if (window.location.hash === '#build-my-outfit') openStudio();
  }

  whenReady(() => {
    document.querySelectorAll('[data-gr-studio]').forEach(initStudio);
  });
})();
